import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Clock, Home, RefreshCw, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { paymentsApi } from '../services/api';

const PaymentStatusPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingRef = searchParams.get('ref');
  const mockStatus = searchParams.get('status'); // For mock payments
  
  const [status, setStatus] = useState('checking'); // checking, paid, pending, failed
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [checkCount, setCheckCount] = useState(0);

  const checkPaymentStatus = useCallback(async () => {
    if (!bookingRef) {
      setStatus('failed');
      setError('No booking reference provided');
      return;
    }

    // Handle mock payment status from URL
    if (mockStatus === 'mock-paid') {
      setStatus('paid');
      setPaymentData({
        booking_reference: bookingRef,
        payment_status: 'paid',
        mock_mode: true
      });
      return;
    }

    try {
      const data = await paymentsApi.checkStatusByReference(bookingRef);
      setPaymentData(data);
      
      if (data.payment_status === 'paid') {
        setStatus('paid');
      } else if (data.payment_status === 'failed') {
        setStatus('failed');
      } else {
        // 'pending', 'processing' (background thread still calling Paynow), or any other state
        setStatus('pending');
      }
    } catch (err) {
      console.error('Failed to check payment status:', err);
      setError(err.message || 'Failed to check payment status');
      setStatus('failed');
    }
  }, [bookingRef, mockStatus]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  // Auto-refresh for pending payments (every 5 seconds, max 36 times = 3 minutes)
  // EcoCash payments can take 60+ seconds as Paynow waits for user to approve on phone
  useEffect(() => {
    if (status === 'pending' && checkCount < 36) {
      const timer = setTimeout(() => {
        setCheckCount(prev => prev + 1);
        checkPaymentStatus();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status, checkCount, checkPaymentStatus]);

  const handleRetry = () => {
    setCheckCount(0);
    setError(null);
    setStatus('checking');
    checkPaymentStatus();
  };

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', ZAR: 'R', GBP: '£', EUR: '€' };
    return symbols[curr] || '$';
  };

  return (
    <div
      className="min-h-screen px-4 py-12"
      style={{ backgroundColor: 'var(--bg-primary)', paddingTop: '100px' }}
      data-testid="payment-status-page"
    >
      <div className="container max-w-2xl">
        <div
          className="p-12 text-center"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)'
          }}
        >
          {/* Checking Status */}
          {status === 'checking' && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6"
                style={{
                  backgroundColor: 'var(--olive-primary)',
                  borderRadius: '50%'
                }}
              >
                <Loader2 size={40} className="animate-spin" style={{ color: 'var(--bg-primary)' }} />
              </div>
              <h1 className="hero-medium mb-4">Checking Payment Status</h1>
              <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                Please wait while we verify your payment...
              </p>
            </>
          )}

          {/* Payment Successful */}
          {status === 'paid' && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6"
                style={{
                  backgroundColor: 'var(--status-success)',
                  borderRadius: '50%'
                }}
              >
                <CheckCircle size={40} style={{ color: 'var(--bg-primary)' }} />
              </div>
              <h1 className="hero-medium mb-4">Payment Successful!</h1>
              <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
                Your payment has been processed successfully.
              </p>
              
              {paymentData?.mock_mode && (
                <div 
                  className="mb-6 p-4 text-left"
                  style={{ 
                    backgroundColor: 'var(--bg-primary)', 
                    border: '1px solid var(--olive-primary)' 
                  }}
                >
                  <p className="body-small" style={{ color: 'var(--olive-primary)' }}>
                    ⚠️ <strong>Mock Mode:</strong> This was a simulated payment. 
                    In production, this would process real payments via Paynow.
                  </p>
                </div>
              )}

              <div
                className="p-6 mb-8 inline-block"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Booking Reference
                </p>
                <p className="heading-2">{bookingRef}</p>
                {paymentData?.total && (
                  <p className="body-regular mt-2" style={{ color: 'var(--text-secondary)' }}>
                    Amount Paid: {getCurrencySymbol(paymentData.currency)}{parseFloat(paymentData.total).toFixed(2)}
                  </p>
                )}
              </div>

              <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>
                A confirmation email has been sent to your email address.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={`/booking-confirmation?ref=${bookingRef}`}>
                  <Button className="btn-primary">View Booking Details</Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" style={{ borderRadius: 0 }}>My Bookings</Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="flex items-center gap-2" style={{ borderRadius: 0 }}>
                    <Home size={16} />
                    Home
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Payment Pending (Mobile Money) */}
          {status === 'pending' && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6"
                style={{
                  backgroundColor: 'var(--olive-primary)',
                  borderRadius: '50%'
                }}
              >
                <Clock size={40} style={{ color: 'var(--bg-primary)' }} />
              </div>
              <h1 className="hero-medium mb-4">Payment Pending</h1>
              <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                Waiting for payment confirmation...
              </p>
              
              <div
                className="p-6 mb-6"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Booking Reference
                </p>
                <p className="heading-3 mb-4">{bookingRef}</p>
                
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Loader2 size={16} className="animate-spin" style={{ color: 'var(--olive-primary)' }} />
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Checking status... ({checkCount}/36)
                  </span>
                </div>
              </div>

              {/* Display Paynow's mobile payment instructions if available */}
              {paymentData?.payment_instructions ? (
                <div
                  className="p-4 mb-6 text-left"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--olive-primary)'
                  }}
                >
                  <p className="body-small font-semibold mb-1" style={{ color: 'var(--olive-primary)' }}>
                    Payment Instructions
                  </p>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {paymentData.payment_instructions}
                  </p>
                </div>
              ) : (
                <p className="body-regular mb-6" style={{ color: 'var(--text-secondary)' }}>
                  If you're paying via mobile money, please check your phone for the payment prompt and approve the transaction.
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleRetry}
                  variant="outline" 
                  className="flex items-center gap-2"
                  style={{ borderRadius: 0 }}
                >
                  <RefreshCw size={16} />
                  Refresh Status
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline" style={{ borderRadius: 0 }}>My Bookings</Button>
                </Link>
              </div>
            </>
          )}

          {/* Payment Failed */}
          {status === 'failed' && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6"
                style={{
                  backgroundColor: '#dc2626',
                  borderRadius: '50%'
                }}
              >
                <XCircle size={40} style={{ color: 'var(--bg-primary)' }} />
              </div>
              <h1 className="hero-medium mb-4">Payment Failed</h1>
              <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                {error || 'Your payment could not be processed.'}
              </p>

              {/* Show Paynow status details when available */}
              {(paymentData?.paynow_status?.status || paymentData?.payment_instructions) && (
                <div
                  className="p-4 mb-6 text-left"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid #dc2626'
                  }}
                >
                  <p className="body-small font-semibold mb-1" style={{ color: '#dc2626' }}>
                    Paynow Status
                  </p>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {paymentData?.paynow_status?.status || paymentData?.payment_instructions}
                  </p>
                </div>
              )}
              
              {bookingRef && (
                <div
                  className="p-6 mb-6"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Booking Reference
                  </p>
                  <p className="heading-3">{bookingRef}</p>
                </div>
              )}

              <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>
                Please try again or contact us if you continue to experience issues.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleRetry}
                  className="btn-primary flex items-center gap-2"
                >
                  <RefreshCw size={16} />
                  Try Again
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline" className="flex items-center gap-2" style={{ borderRadius: 0 }}>
                    <CreditCard size={16} />
                    Retry Payment
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" style={{ borderRadius: 0 }}>Contact Support</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusPage;
